import styled, { keyframes } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../../../../../components/AppShell/styles";
import { fireRingWrapper } from "../../../../../../styles/fireRing";

/* One line of the summary: icon, name/value, and (on the address) its status on the right */
export const SummaryRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;

  & + & {
    padding-top: 1rem;
    border-top: 1px solid var(--color-neutral-base);
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 0.75rem;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .name {
    font-family: Inter, sans-serif;
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .value {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    min-width: 0;
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    white-space: nowrap;
  }

  /* A long address shortens, its tag stays */
  .address {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Whether the Keepo is on the air yet */
export const Status = styled.span<{ $online: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  gap: 0.375rem;
  align-items: center;
  margin-left: auto;
  padding: 0.25rem 0.625rem;
  font-family: Inter, sans-serif;
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--color-neutral-150);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 100px;

  &::before {
    width: 7px;
    height: 7px;
    content: "";
    background: ${props => (props.$online ? "var(--color-brand-plus)" : "var(--color-neutral-light)")};
    border-radius: 50%;
    box-shadow: ${props => (props.$online ? "0 0 8px var(--color-brand-plus)" : "none")};
  }
`;

/* What publishing does, under the card */
export const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
  line-height: 1.1875rem;
  color: var(--color-neutral-150);
`;

/* The publish button with the fire ring around it (see fireRing), and the 🔥 flickering */
const flicker = keyframes`
  0%, 100% {
    transform: scale(1) rotate(-2deg);
  }
  50% {
    transform: scale(1.05) rotate(2deg);
  }
`;

export const FireButton = styled.div`
  ${fireRingWrapper}

  > div {
    width: 100%;
    /* The label on one line: on phones the button takes the bar's free width, and a smaller type if needed */
    white-space: nowrap;
  }

  @media not all and (min-width: ${DESKTOP_BREAKPOINT}) {
    /* Beats the bar's "main action takes half" rule */
    &&&& {
      /* All the bar's free width whatever the label says (a 0 basis), so it doesn't resize while publishing */
      flex: 1 1 0;
      min-width: 0;
    }

    > div {
      padding-right: 1rem;
      padding-left: 1rem;
      font-size: 0.8125rem;
    }
  }

  .symbol {
    margin-right: 0.5rem;
  }

  /* While publishing: the disabled look, with what's going on still readable, and the fire around it off */
  > div[aria-busy="true"] {
    color: var(--color-neutral-150);
  }

  &:has(> div[aria-busy="true"]) {
    .fire-ring,
    .fire-halo {
      opacity: 0;
    }
  }

  /* While publishing: the loader (28px with its arc) is taller than the line; it doesn't make the button
     grow, only the label changes */
  .symbol:not(.fire) {
    margin-top: -6px;
    margin-bottom: -6px;
  }

  .fire {
    display: inline-block;
    animation: ${flicker} 3s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .fire {
      animation: none;
    }
  }
`;

/* Right after subscribing: the payment went through (or the boleto was generated), now it's publishing */
export const PaidBanner = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.875rem 1rem;
  /* Over the summary, apart from it (the section's gap is for the label and its card) */
  margin-bottom: 0.75rem;
  font-family: Inter, sans-serif;
  color: var(--color-neutral-white);
  background: color-mix(in srgb, var(--color-feedback-success) 14%, transparent);
  border: 1px solid color-mix(in srgb, var(--color-feedback-success) 45%, transparent);
  border-radius: 0.875rem;

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    /* Dark check on the green: white on it doesn't read */
    color: var(--color-neutral-darkness);
    background: var(--color-feedback-success);
    border-radius: 50%;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
    font-size: 0.8125rem;
    line-height: 1.125rem;
    /* Light text: the grey of the other notes is too dark over the green */
    color: var(--color-neutral-lightness);
  }

  b {
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }
`;
